import { BACKEND_BASE, getAccessToken, authHeaders } from '@/features/chatbot/engine/backend';

// ---------------------------------------------------------------------------
// youtubeService
//
// Thin wrappers over the two backend routes:
//   POST /api/youtube/search   — search YouTube by query (paged)
//   POST /api/youtube/resolve  — authoritative metadata for a single videoId
//
// Plus URL parsing — extract a YouTube videoId from any supported URL form.
// ---------------------------------------------------------------------------

export interface YouTubeSearchResult {
  videoId: string;
  title: string;
  channelName: string;
  thumbnailUrl: string | null;
  publishedAt: string | null;
}

export interface YouTubeSearchPage {
  items: YouTubeSearchResult[];
  nextPageToken: string | null;
}

export interface YouTubeResolved {
  videoId: string;
  title: string;
  channelName: string;
  channelId: string;
  thumbnailUrl: string | null;
  durationISO: string | null;
  privacyStatus: 'public' | 'unlisted' | string;
  regionBlocked: string[] | null;
  regionAllowed: string[] | null;
}

export type YouTubeErrorCode =
  | 'NOT_FOUND'
  | 'PRIVATE'
  | 'NOT_EMBEDDABLE'
  | 'UPSTREAM_ERROR'
  | 'NOT_CONFIGURED'
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'UNKNOWN';

export class YouTubeServiceError extends Error {
  readonly code: YouTubeErrorCode;
  readonly status: number;
  constructor(code: YouTubeErrorCode, status: number, message?: string) {
    super(message ?? code);
    this.name = 'YouTubeServiceError';
    this.code = code;
    this.status = status;
  }
}

const SEARCH_ENDPOINT = `${BACKEND_BASE}/api/youtube/search`;
const RESOLVE_ENDPOINT = `${BACKEND_BASE}/api/youtube/resolve`;

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/**
 * Extract a YouTube videoId from any of the supported URL forms. Accepts:
 *   - https://www.youtube.com/watch?v=XXXX
 *   - https://youtu.be/XXXX
 *   - https://www.youtube.com/embed/XXXX
 *   - https://www.youtube.com/shorts/XXXX
 *   - https://www.youtube-nocookie.com/embed/XXXX
 *   - bare 11-character video id
 */
export function extractYouTubeId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  if (VIDEO_ID_RE.test(trimmed)) return trimmed;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');
  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    return VIDEO_ID_RE.test(id) ? id : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
    const v = url.searchParams.get('v');
    if (v && VIDEO_ID_RE.test(v)) return v;
    const segments = url.pathname.split('/').filter(Boolean);
    // /embed/<id>, /shorts/<id>, /v/<id>, /live/<id>
    if (segments.length >= 2 && ['embed', 'shorts', 'v', 'live'].includes(segments[0])) {
      const id = segments[1];
      return VIDEO_ID_RE.test(id) ? id : null;
    }
  }
  return null;
}

async function postJson<T>(endpoint: string, body: unknown): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (response.ok) return (await response.json()) as T;

  let code: YouTubeErrorCode = 'UNKNOWN';
  try {
    const errBody = (await response.json()) as { code?: string };
    if (typeof errBody.code === 'string') {
      code = errBody.code as YouTubeErrorCode;
    }
  } catch {
    // Non-JSON error body — fall through with UNKNOWN.
  }
  throw new YouTubeServiceError(code, response.status);
}

export async function searchYouTubeVideos(
  q: string,
  pageToken?: string
): Promise<YouTubeSearchPage> {
  return postJson<YouTubeSearchPage>(SEARCH_ENDPOINT, {
    q,
    ...(pageToken ? { pageToken } : {}),
  });
}

export async function resolveYouTubeVideo(videoId: string): Promise<YouTubeResolved> {
  if (!VIDEO_ID_RE.test(videoId)) {
    throw new YouTubeServiceError('VALIDATION_ERROR', 400, 'Invalid videoId');
  }
  return postJson<YouTubeResolved>(RESOLVE_ENDPOINT, { videoId });
}
